import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Cookie, Settings, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Switch } from './ui/switch';

const COOKIE_CONSENT_KEY = 'fth_cookie_consent';
const GA_MEASUREMENT_ID = 'G-CWZJE4GHWJ';

// Track GA script state (module-level to persist across re-renders)
let gaScriptLoaded = false;
let gaScriptLoading = false;

// Safe gtag wrapper - only calls gtag if it exists
const safeGtag = (...args) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
    return true;
  }
  return false;
};

// Clear GA cookies (best-effort)
const clearGACookies = () => {
  const gaCookies = ['_ga', '_gid', '_gat', '_ga_' + GA_MEASUREMENT_ID.replace('G-', '')];
  const domains = [window.location.hostname, '.' + window.location.hostname];
  
  gaCookies.forEach(cookieName => {
    domains.forEach(domain => {
      // Try to clear with different path combinations
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  });
  
  console.log('[Analytics] Attempted to clear GA cookies');
};

// Set GA disable flag
const setGADisabled = (disabled) => {
  if (typeof window !== 'undefined') {
    window['ga-disable-' + GA_MEASUREMENT_ID] = disabled;
    console.log(`[Analytics] GA disable flag set to: ${disabled}`);
  }
};

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });
  
  // Ref to store pending consent to apply after script loads
  const pendingConsentRef = useRef(null);

  // Listen for external requests to open preferences
  useEffect(() => {
    const handleOpenPreferences = () => {
      setShowBanner(true);
      setShowSettings(true);
    };
    
    window.addEventListener('openCookiePreferences', handleOpenPreferences);
    return () => window.removeEventListener('openCookiePreferences', handleOpenPreferences);
  }, []);

  // Apply consent settings to GA (only if GA is loaded)
  const applyConsentToGA = useCallback((consent) => {
    if (!gaScriptLoaded || typeof window.gtag !== 'function') {
      console.log('[Analytics] GA not loaded, consent queued');
      return false;
    }
    
    // Apply analytics consent
    safeGtag('consent', 'update', {
      'analytics_storage': consent.analytics ? 'granted' : 'denied'
    });
    
    // Apply marketing consent
    safeGtag('consent', 'update', {
      'ad_storage': consent.marketing ? 'granted' : 'denied',
      'ad_user_data': consent.marketing ? 'granted' : 'denied',
      'ad_personalization': consent.marketing ? 'granted' : 'denied'
    });
    
    console.log(`[Analytics] Consent applied - analytics: ${consent.analytics}, marketing: ${consent.marketing}`);
    return true;
  }, []);

  // Load GA script dynamically - ONLY when analytics consent is granted
  const loadGAScript = useCallback((consent) => {
    // Check if analytics is disabled
    if (window['ga-disable-' + GA_MEASUREMENT_ID]) {
      console.log('[Analytics] GA is disabled, not loading script');
      return;
    }
    
    if (gaScriptLoaded) {
      // Script already loaded, just apply consent
      applyConsentToGA(consent);
      return;
    }
    
    if (gaScriptLoading) {
      // Script is loading, queue the consent
      pendingConsentRef.current = consent;
      return;
    }
    
    gaScriptLoading = true;
    pendingConsentRef.current = consent;
    
    // Initialize dataLayer and gtag function first
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };
    
    // Create and inject the GA script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    
    script.onload = () => {
      gaScriptLoaded = true;
      gaScriptLoading = false;
      
      // Get the consent to apply (from ref)
      const consentToApply = pendingConsentRef.current || consent;
      
      // Initialize GA with consent mode defaults first
      window.gtag('js', new Date());
      window.gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
      
      // Configure GA
      window.gtag('config', GA_MEASUREMENT_ID, {
        'anonymize_ip': true,
        'send_page_view': consentToApply.analytics // Only send pageview if analytics granted
      });
      
      // Now apply the actual consent
      applyConsentToGA(consentToApply);
      
      console.log('[Analytics] GA script loaded and initialized');
      pendingConsentRef.current = null;
    };
    
    script.onerror = () => {
      gaScriptLoading = false;
      pendingConsentRef.current = null;
      console.error('[Analytics] Failed to load GA script');
    };
    
    document.head.appendChild(script);
  }, [applyConsentToGA]);

  // Main consent application function
  const applyConsent = useCallback((consent) => {
    if (consent.analytics) {
      // Enable analytics - unset disable flag and load/apply
      setGADisabled(false);
      loadGAScript(consent);
    } else {
      // Disable analytics
      setGADisabled(true);
      
      // If GA is loaded, deny consent
      if (gaScriptLoaded) {
        safeGtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }
      
      // Clear GA cookies (best-effort)
      clearGACookies();
    }
    
    // Apply marketing consent if GA is loaded
    if (gaScriptLoaded) {
      safeGtag('consent', 'update', {
        'ad_storage': consent.marketing ? 'granted' : 'denied',
        'ad_user_data': consent.marketing ? 'granted' : 'denied',
        'ad_personalization': consent.marketing ? 'granted' : 'denied'
      });
    }

    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consent }));
  }, [loadGAScript]);

  // Initialize on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    if (!savedConsent) {
      // No consent saved - show banner after delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
    
    try {
      const parsed = JSON.parse(savedConsent);
      setPreferences(parsed);
      
      // Apply saved consent
      if (parsed.analytics) {
        // Analytics was granted - load GA
        setGADisabled(false);
        loadGAScript(parsed);
      } else {
        // Analytics was denied - ensure GA is disabled
        setGADisabled(true);
      }
    } catch {
      // Invalid stored consent - show banner
      setShowBanner(true);
    }
  }, [loadGAScript]);

  const savePreferences = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    applyConsent(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const handleRejectAll = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    savePreferences(essentialOnly);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-2xl shadow-black/10 overflow-hidden">
          {/* Main Banner */}
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex w-12 h-12 bg-emerald-500/10 rounded-xl items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-emerald-600 sm:hidden" />
                  We Value Your Privacy
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                  Essential cookies are necessary for the website to function. Analytics and marketing cookies require your consent.
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 text-sm underline underline-offset-2"
                  >
                    Privacy Policy
                  </a>
                  <span className="text-gray-400">•</span>
                  <a 
                    href="/cookies" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 text-sm underline underline-offset-2"
                  >
                    Cookie Policy
                  </a>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button
                onClick={handleAcceptAll}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
              >
                Accept All
              </Button>
              <Button
                onClick={handleRejectAll}
                variant="outline"
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              >
                Reject All
              </Button>
              <Button
                onClick={() => setShowSettings(!showSettings)}
                variant="ghost"
                className="flex-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <Settings className="w-4 h-4 mr-2" />
                Customize
                {showSettings ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="border-t border-gray-200 bg-gray-50 p-6">
              <h4 className="text-gray-900 font-medium mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-600" />
                Cookie Preferences
              </h4>
              
              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center gap-2">
                      <p className="text-gray-900 font-medium">Essential Cookies</p>
                      <span className="text-xs bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded-full">Required</span>
                    </div>
                    <p className="text-gray-500 text-sm mt-1">
                      Necessary for the website to function. These cannot be disabled.
                    </p>
                  </div>
                  <Switch 
                    checked={true} 
                    disabled 
                    className="data-[state=checked]:bg-emerald-500 opacity-50"
                  />
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex-1 pr-4">
                    <p className="text-gray-900 font-medium">Analytics Cookies</p>
                    <p className="text-gray-500 text-sm mt-1">
                      Help us understand how visitors interact with our website to improve user experience.
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, analytics: checked })}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex-1 pr-4">
                    <p className="text-gray-900 font-medium">Marketing Cookies</p>
                    <p className="text-gray-500 text-sm mt-1">
                      Used to deliver relevant advertisements and track campaign performance.
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Export function to check consent status
export const getCookieConsent = () => {
  try {
    const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {}
  return { essential: true, analytics: false, marketing: false };
};

// Export function to check if GA is currently enabled
export const isAnalyticsEnabled = () => {
  const consent = getCookieConsent();
  return consent.analytics && !window['ga-disable-' + GA_MEASUREMENT_ID];
};

// Export function to update consent programmatically
export const updateCookieConsent = (newPreferences) => {
  const prefs = { essential: true, ...newPreferences };
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
  
  // Apply consent changes
  if (prefs.analytics) {
    setGADisabled(false);
  } else {
    setGADisabled(true);
    clearGACookies();
  }
  
  // Update GA consent if loaded
  if (gaScriptLoaded && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': prefs.analytics ? 'granted' : 'denied',
      'ad_storage': prefs.marketing ? 'granted' : 'denied',
      'ad_user_data': prefs.marketing ? 'granted' : 'denied',
      'ad_personalization': prefs.marketing ? 'granted' : 'denied'
    });
  }
  
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: prefs }));
};

// Export function to open cookie preferences without clearing consent
export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent('openCookiePreferences'));
};

// Export function to reset consent (clears storage and reloads - use sparingly)
export const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  setGADisabled(true);
  clearGACookies();
  window.location.reload();
};

export default CookieConsentBanner;
